import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import Cropper from "react-easy-crop";
import {
  adminLogin,
  fetchStats,
  fetchPlayers,
  fetchAdminTeams,
  getPlayerFileUrl,
  fetchPlayerCard,
  savePlayerCard,
  fetchPlayerCardImage,
  exportAllCards,
  approvePlayer,
  rejectPlayer,
  deletePlayer,
  exportPlayers,
  createTeam,
  updateTeam,
  deleteTeam,
  fetchTournamentSettings,
  updateTournamentSettings,
  uploadFile,
} from "../api";
import IdCard from "../components/IdCard";
import "./AdminPage.css";

// ── SVG Icons ─────────────────────────────────────────────
const IconUser = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);
const IconPhoto = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);
const IconDoc = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const IconTrash = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
const IconCheck = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconX = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconDownload = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IconImage = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

const IconCard = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <circle cx="8" cy="11" r="2" />
    <path d="M6 16c0-1.5 1-2.5 2-2.5s2 1 2 2.5" />
    <line x1="14" y1="10" x2="19" y2="10" />
    <line x1="14" y1="14" x2="19" y2="14" />
  </svg>
);

// 카드 사진 칸 비율 (사진 칸 폭 ÷ 높이 ≈ 1.157). 카드 CSS 변경 시 함께 조정.
const PHOTO_ASPECT = 347 / 300;

// 원본 이미지에서 크롭 영역만 잘라 data URI 로 반환.
// 모바일 캔버스 메모리 한계를 피하려 카드 표시 크기 수준으로 축소하고 JPEG 로 인코딩.
async function getCroppedImg(src, cropPixels) {
  const image = new Image();
  image.src = src;
  await image.decode();
  const MAX_W = 800; // 카드 사진 칸(약 347px) × 2배 해상도면 충분
  const scale = Math.min(1, MAX_W / cropPixels.width);
  const w = Math.max(1, Math.round(cropPixels.width * scale));
  const h = Math.max(1, Math.round(cropPixels.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    w,
    h,
  );
  return canvas.toDataURL("image/jpeg", 0.9);
}

const IS_SAFARI =
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)));

// 카드 DOM(node)을 흰 여백을 덧댄 PNG data URL 로 렌더
async function renderCardPng(node) {
  const pixelRatio = 2;
  const opts = { pixelRatio, backgroundColor: "#ffffff" };
  // iOS Safari 만 첫 캡처에서 이미지를 누락 → 1회 워밍업. 그 외는 1회 캡처(빠름).
  if (IS_SAFARI) await toPng(node, opts);
  const cardUrl = await toPng(node, opts);
  const img = new Image();
  img.src = cardUrl;
  await img.decode();
  const pad = 28 * pixelRatio;
  const canvas = document.createElement("canvas");
  canvas.width = img.width + pad * 2;
  canvas.height = img.height + pad * 2;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, pad, pad);
  return canvas.toDataURL("image/png");
}

// 터치 기기(모바일/태블릿) 여부 — 데스크톱은 공유 시트 대신 곧장 다운로드
const IS_TOUCH_DEVICE =
  typeof navigator !== "undefined" &&
  (navigator.maxTouchPoints > 0 ||
    (typeof window !== "undefined" &&
      window.matchMedia?.("(pointer: coarse)").matches));

// 모바일: 공유 시트("사진에 저장") 우선, 미지원 시 일반 다운로드(Files)로 폴백
// 데스크톱: 항상 일반 다운로드 (Chrome/Edge가 파일 공유를 지원해도 공유 창을 띄우지 않음)
async function saveCardImage(dataUrl, filename) {
  const name = filename.replace(/\s+/g, "_");
  if (IS_TOUCH_DEVICE) {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], name, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: name });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return; // 사용자가 공유 취소
      // 그 외 오류는 아래 다운로드로 폴백
    }
  }
  const link = document.createElement("a");
  link.download = name;
  link.href = dataUrl;
  link.click();
}

// ── ID Card 생성 모달 (크롭 → S3 저장) ────────────────────
function IdCardGenerateModal({ player, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPhoto, setCroppedPhoto] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    fetchPlayerCard(player.id)
      .then((r) => setData(r.data))
      .catch((e) =>
        setError(
          e.response?.data?.error ||
            (e.response?.status === 403
              ? "승인된 선수만 ID 카드를 생성할 수 있습니다."
              : "카드 정보를 불러오지 못했습니다."),
        ),
      );
  }, [player.id]);

  const onCropComplete = useCallback(
    async (_area, areaPixels) => {
      if (!data?.photo_url || !areaPixels?.width) return;
      try {
        setCroppedPhoto(await getCroppedImg(data.photo_url, areaPixels));
      } catch {
        /* 이전 크롭 유지 */
      }
    },
    [data],
  );

  // 크롭 결과를 사진으로 사용 (크롭 전이면 원본 사용)
  const cardData = data
    ? { ...data, photo_url: croppedPhoto || data.photo_url }
    : null;

  const save = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    setError("");
    try {
      const dataUrl = await renderCardPng(cardRef.current);
      await savePlayerCard(player.id, dataUrl);
      onSaved();
    } catch {
      setError("카드 생성에 실패했습니다.");
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal card idcard-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>ID Card 생성 — {player.full_name}</h3>
        {error && <p className="error-msg">{error}</p>}
        {!data && !error && <p className="text-muted">Loading...</p>}
        {data && (
          <>
            {/* 사진 크롭 (드래그 이동 / 슬라이더로 확대) */}
            {data.photo_url && (
              <div className="idcard-crop-wrap">
                <div className="idcard-cropper">
                  <Cropper
                    image={data.photo_url}
                    crop={crop}
                    zoom={zoom}
                    aspect={PHOTO_ASPECT}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
                <div className="idcard-cropper-controls">
                  <span>확대</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                  />
                </div>
              </div>
            )}

            {/* 미리보기 (축소) */}
            <div className="idcard-preview">
              <div className="idcard-scale">
                <IdCard data={cardData} />
              </div>
            </div>
            {/* 캡처용 (원본 크기, 화면 밖 — 변형 없이 캡처) */}
            <div className="idcard-capture" aria-hidden="true">
              <IdCard ref={cardRef} data={cardData} />
            </div>
          </>
        )}
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={save}
            disabled={!data || saving}
          >
            {saving ? "저장 중..." : "카드 생성"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ID Card 조회 모달 (저장된 카드 표시) ──────────────────
function IdCardViewModal({ player, onClose, onRegenerate }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPlayerCardImage(player.id)
      .then((r) => setCard(r.data))
      .catch(() => setError("카드를 불러오지 못했습니다."));
  }, [player.id]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal card idcard-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>ID Card — {player.full_name}</h3>
        {error && <p className="error-msg">{error}</p>}
        {!card && !error && <p className="text-muted">Loading...</p>}
        {card && (
          <>
            <div className="idcard-view-meta">
              <span>{card.id_number}</span>
              <span>
                {card.issued_at} ~ {card.expires_at}
              </span>
            </div>
            <div className="idcard-view-img">
              <img src={card.image} alt="ID Card" />
            </div>
          </>
        )}
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>
            Close
          </button>
          <button className="btn-ghost" onClick={() => onRegenerate(player)}>
            재발급
          </button>
          <button
            className="btn-primary"
            onClick={() =>
              saveCardImage(
                card.image,
                `${card.id_number}_${player.full_name}.png`,
              )
            }
            disabled={!card}
          >
            <IconDownload /> Download PNG
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────
function LoginForm({ onLogin }) {
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await adminLogin(creds.username, creds.password);
      localStorage.setItem("admin_token", res.data.access_token);
      onLogin(res.data.username);
    } catch {
      setError("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <div className="login-logo">
          <img
            src="/images/kasausa_icon.png"
            alt="KFAUS"
            className="nav-logo-img"
          />
          <div className="login-logo-text">
            재미대한축구협회
            <span>Korean American Soccer Association</span>
          </div>
        </div>
        <h1>Admin Login</h1>
        <input
          type="text"
          placeholder="Username"
          value={creds.username}
          onChange={(e) =>
            setCreds((c) => ({ ...c, username: e.target.value }))
          }
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={creds.password}
          onChange={(e) =>
            setCreds((c) => ({ ...c, password: e.target.value }))
          }
          required
        />
        {error && <p className="error-msg">{error}</p>}
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? "Logging in..." : "Log In"}
        </button>
      </form>
    </div>
  );
}

// ── Reject Modal ──────────────────────────────────────────
function RejectModal({ player, onConfirm, onCancel }) {
  const [note, setNote] = useState("");
  return (
    <div className="modal-overlay">
      <div className="modal card">
        <h3>Reject — {player.full_name}</h3>
        <p className="modal-sub">Reason (optional)</p>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Incomplete documents"
        />
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-danger" onClick={() => onConfirm(note)}>
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Confirm Modal ──────────────────────────────────
function DeleteConfirmModal({ player, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <h3>선수 삭제</h3>
        <p
          className="modal-sub"
          style={{
            fontSize: 13,
            color: "rgba(10,22,40,0.7)",
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: "#0a1628" }}>{player.full_name}</strong>{" "}
          선수를 삭제하시겠습니까?
          <br />
          <span style={{ fontSize: 12, color: "#c8102e" }}>
            이 작업은 되돌릴 수 없습니다.
          </span>
        </p>
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onCancel}>
            취소
          </button>
          <button
            className="btn-danger"
            style={{ background: "#c8102e", color: "#fff" }}
            onClick={onConfirm}
          >
            삭제
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Player Detail Modal ───────────────────────────────────
function PlayerDetailModal({ player, onClose, onApprove, onReject, onMakeCard }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [licenseUrl, setLicenseUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (player.photo_url) {
      getPlayerFileUrl(player.id, "photo")
        .then((r) => setPhotoUrl(r.data.url))
        .catch(() => {});
    }
    if (player.driver_license_url) {
      getPlayerFileUrl(player.id, "license")
        .then((r) => setLicenseUrl(r.data.url))
        .catch(() => {});
    }
  }, [player.id, player.photo_url, player.driver_license_url]);

  if (previewUrl)
    return (
      <div
        className="modal-overlay preview-overlay"
        onClick={() => setPreviewUrl(null)}
      >
        <div className="preview-lightbox" onClick={(e) => e.stopPropagation()}>
          <button className="preview-close" onClick={() => setPreviewUrl(null)}>
            <IconX />
          </button>
          <img src={previewUrl} alt="preview" className="preview-img" />
        </div>
      </div>
    );

  const initials = player.full_name
    ? player.full_name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-clean card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-clean-header">
          <div className="modal-clean-avatar">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={player.full_name}
                className="modal-clean-avatar-img"
              />
            ) : (
              <span className="modal-clean-avatar-initials">{initials}</span>
            )}
          </div>
          <div className="modal-clean-identity">
            <div className="modal-clean-name">{player.full_name}</div>
            <div className="modal-clean-sub">
              {player.team_name}
              {player.position ? ` · ${player.position}` : ""}
              {player.jersey_number ? ` #${player.jersey_number}` : ""}
            </div>
          </div>
          <span className={`badge badge-${player.status}`}>
            {player.status}
          </span>
        </div>

        <div className="modal-clean-section">
          <div className="modal-clean-section-label">Personal Info</div>
          <div className="modal-clean-grid">
            <div className="modal-clean-cell">
              <span>Date of Birth</span>
              <strong>{player.date_of_birth || "—"}</strong>
            </div>
            <div className="modal-clean-cell">
              <span>Nationality</span>
              <strong>
                {player.nationality}
                {player.is_pr === 1
                  ? " · PR"
                  : player.is_pr === 0
                    ? " · Non-PR"
                    : ""}
              </strong>
            </div>
            <div className="modal-clean-cell modal-clean-cell-full">
              <span>Email</span>
              <strong>{player.email}</strong>
            </div>
            <div className="modal-clean-cell">
              <span>Phone</span>
              <strong>{player.phone || "—"}</strong>
            </div>
            {player.name_korean && (
              <div className="modal-clean-cell">
                <span>한글 이름</span>
                <strong>{player.name_korean}</strong>
              </div>
            )}
            {player.admin_note && (
              <div className="modal-clean-cell modal-clean-cell-full">
                <span>Admin Note</span>
                <strong>{player.admin_note}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="modal-clean-section">
          <div className="modal-clean-section-label">Payment</div>
          <div className="modal-clean-grid">
            <div className="modal-clean-cell">
              <span>Status</span>
              <strong
                className={
                  player.payment_status === "paid" ? "modal-clean-paid" : ""
                }
              >
                {player.payment_status}
                {player.payment_method ? ` (${player.payment_method})` : ""}
              </strong>
            </div>
            <div className="modal-clean-cell">
              <span>Registered</span>
              <strong>
                {player.registered_at
                  ? new Date(player.registered_at).toLocaleDateString("ko-KR")
                  : "—"}
              </strong>
            </div>
          </div>
        </div>

        {(photoUrl || licenseUrl) && (
          <div className="modal-clean-files">
            {photoUrl && (
              <button
                className="modal-clean-file-btn"
                onClick={() => setPreviewUrl(photoUrl)}
              >
                <IconImage /> Picture
              </button>
            )}
            {licenseUrl && (
              <button
                className="modal-clean-file-btn"
                onClick={() => setPreviewUrl(licenseUrl)}
              >
                <IconDoc /> I.D.
              </button>
            )}
          </div>
        )}

        <div className="modal-clean-actions">
          <button className="btn-ghost" onClick={onClose}>
            Close
          </button>
          {player.status === "approved" && (
            <button
              className="btn-ghost"
              onClick={() => {
                onMakeCard(player);
                onClose();
              }}
            >
              <IconCard /> {player.card_url ? "ID Card 보기" : "ID Card 생성"}
            </button>
          )}
          {player.status === "pending" && (
            <>
              <button
                className="btn-primary"
                onClick={() => {
                  onApprove(player);
                  onClose();
                }}
              >
                Approve
              </button>
              <button
                className="btn-danger"
                onClick={() => {
                  onReject(player);
                  onClose();
                }}
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Player Row ────────────────────────────────────────────
function PlayerRow({ player, onApprove, onReject, onDelete, onView }) {
  return (
    <tr onClick={() => onView(player)} className="clickable-row">
      <td>
        <div className="player-name">{player.full_name}</div>
        {/* <div className="player-sub">{player.email}</div> */}
      </td>
      <td>{player.team_name}</td>
      <td>{player.date_of_birth}</td>
      <td>
        {player.position || "—"}
        {player.jersey_number ? ` #${player.jersey_number}` : ""}
      </td>
      <td>
        {player.nationality}
        {player.is_pr === 1 ? " · PR" : player.is_pr === 0 ? " · Non-PR" : ""}
      </td>
      <td>
        <span className={`badge badge-${player.status}`}>{player.status}</span>
      </td>
      {/* <td>
                <span
                    className={`badge ${player.payment_status === "paid" ? "badge-approved" : "badge-pending"}`}
                >
                    {player.payment_status}
                </span>
            </td> */}
      <td onClick={(e) => e.stopPropagation()}>
        <div className="row-actions">
          {player.status === "pending" && (
            <>
              <button
                className="btn-approve"
                title="Approve"
                onClick={() => onApprove(player)}
              >
                <IconCheck />
              </button>
              <button
                className="btn-reject"
                title="Reject"
                onClick={() => onReject(player)}
              >
                <IconX />
              </button>
            </>
          )}
          <button
            className="btn-del"
            title="Delete"
            onClick={() => onDelete(player)}
          >
            <IconTrash />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Team Players Modal ────────────────────────────────────
function TeamPlayersModal({ team, onClose, onViewPlayer }) {
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    fetchPlayers({ team_id: team.id })
      .then((r) => setPlayers(r.data))
      .catch(() => {});
  }, [team.id]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-team card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="team-modal-header">
          <div>
            <h3>{team.name}</h3>
            <p className="modal-sub">
              {team.player_count} / {team.max_players} players registered
            </p>
          </div>
          <div className="team-modal-bar">
            <div
              className="team-modal-bar-fill"
              style={{
                width: `${Math.round((team.player_count / team.max_players) * 100)}%`,
              }}
            />
          </div>
        </div>
        <div className="team-modal-table-wrap">
          <table className="players-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Pos / #</th>
                <th>Status</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {players.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-row">
                    No players
                  </td>
                </tr>
              ) : (
                players.map((p) => (
                  <tr
                    key={p.id}
                    className="clickable-row"
                    onClick={() => {
                      onViewPlayer(p);
                      onClose();
                    }}
                  >
                    <td>
                      <div className="player-name">{p.full_name}</div>
                      <div className="player-sub">{p.email}</div>
                    </td>
                    <td>
                      {p.position || "—"}
                      {p.jersey_number ? ` #${p.jersey_number}` : ""}
                    </td>
                    <td>
                      <span className={`badge badge-${p.status}`}>
                        {p.status}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${p.payment_status === "paid" ? "badge-approved" : "badge-pending"}`}
                      >
                        {p.payment_status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Teams Tab ─────────────────────────────────────────────
function TeamsTab({ onSelectTeam }) {
  const [teams, setTeams] = useState([]);
  const [newName, setNewName] = useState("");
  const [newInitial, setNewInitial] = useState("");
  const [newMax, setNewMax] = useState(25);
  const [newLogo, setNewLogo] = useState(null);
  const [newLogoPreview, setNewLogoPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editLogo, setEditLogo] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  const newLogoRef = useRef();
  const editLogoRef = useRef();

  const load = () => fetchAdminTeams().then((r) => setTeams(r.data));
  useEffect(() => {
    load();
  }, []);

  const handleLogoSelect = (file, setFile, setPreview) => {
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const uploadLogo = async (file, teamName) => {
    if (!file) return null;
    const res = await uploadFile(file, "logo", {
      team_name: teamName || "unknown",
    });
    return res.data.key;
  };

  const add = async () => {
    if (!newName.trim()) return;
    if (!newInitial.trim()) {
      alert("팀 이니셜을 입력하세요 (예: LA).");
      return;
    }
    const logoKey = await uploadLogo(newLogo, newName.trim());
    await createTeam({
      name: newName.trim(),
      initial: newInitial.trim().toUpperCase(),
      max_players: newMax,
      logo_url: logoKey,
    });
    setNewName("");
    setNewInitial("");
    setNewMax(25);
    setNewLogo(null);
    setNewLogoPreview(null);
    load();
  };

  const save = async (team) => {
    if (!(editing.initial || "").trim()) {
      alert("팀 이니셜을 입력하세요 (예: LA).");
      return;
    }
    const logoKey = editLogo
      ? await uploadLogo(editLogo, editing.name)
      : editing.logo_url;
    await updateTeam(team.id, {
      name: editing.name,
      initial: (editing.initial || "").trim().toUpperCase(),
      max_players: editing.max_players,
      logo_url: logoKey,
    });
    setEditing(null);
    setEditLogo(null);
    setEditLogoPreview(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete team? All players will be removed.")) return;
    await deleteTeam(id);
    load();
  };

  return (
    <div className="teams-tab">
      <div className="add-team card">
        <h3>Add New Team</h3>
        <div className="add-team-row">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Team name"
          />
          <input
            value={newInitial}
            onChange={(e) => setNewInitial(e.target.value.toUpperCase())}
            placeholder="Initial"
            maxLength={5}
            style={{ width: 90, textTransform: "uppercase" }}
          />
          <input
            type="number"
            value={newMax}
            onChange={(e) => setNewMax(+e.target.value)}
            placeholder="Max players"
            style={{ width: 130 }}
          />
          <div
            className="logo-upload-wrap"
            onClick={() => newLogoRef.current.click()}
          >
            {newLogoPreview ? (
              <img src={newLogoPreview} alt="logo" className="logo-preview" />
            ) : (
              <span className="logo-placeholder">Logo</span>
            )}
            <input
              ref={newLogoRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) =>
                handleLogoSelect(
                  e.target.files[0],
                  setNewLogo,
                  setNewLogoPreview,
                )
              }
            />
          </div>
          <button className="btn-primary" onClick={add}>
            Add Team
          </button>
        </div>
      </div>
      <div className="teams-list">
        {teams.map((t) => (
          <div className="team-row card" key={t.id}>
            {editing?.id === t.id ? (
              <div className="team-edit">
                <input
                  value={editing.name}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      name: e.target.value,
                    })
                  }
                />
                <input
                  value={editing.initial || ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      initial: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="Initial"
                  maxLength={5}
                  style={{ width: 90, textTransform: "uppercase" }}
                />
                <input
                  type="number"
                  value={editing.max_players}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      max_players: +e.target.value,
                    })
                  }
                  style={{ width: 100 }}
                />
                <div
                  className="logo-upload-wrap"
                  onClick={() => editLogoRef.current.click()}
                >
                  {editLogoPreview ? (
                    <img
                      src={editLogoPreview}
                      alt="logo"
                      className="logo-preview"
                    />
                  ) : (
                    <span className="logo-placeholder">Logo</span>
                  )}
                  <input
                    ref={editLogoRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) =>
                      handleLogoSelect(
                        e.target.files[0],
                        setEditLogo,
                        setEditLogoPreview,
                      )
                    }
                  />
                </div>
                <button className="btn-primary" onClick={() => save(t)}>
                  Save
                </button>
                <button
                  className="btn-ghost"
                  onClick={() => {
                    setEditing(null);
                    setEditLogo(null);
                    setEditLogoPreview(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="team-info">
                <div>
                  <strong
                    className="team-name-link"
                    onClick={() => onSelectTeam(t)}
                  >
                    {t.name}
                  </strong>
                  {t.initial ? (
                    <span className="team-initial-badge">{t.initial}</span>
                  ) : (
                    <span className="team-initial-badge team-initial-missing">
                      이니셜 미설정
                    </span>
                  )}
                  <span className="team-count">
                    {t.player_count}/{t.max_players} players
                  </span>
                </div>
                <div className="row-actions">
                  <button
                    className="btn-ghost"
                    onClick={() => setEditing({ ...t })}
                  >
                    Edit
                  </button>
                  <button className="btn-danger" onClick={() => del(t.id)}>
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tournament Settings Tab ───────────────────────────────
function TournamentTab() {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchTournamentSettings().then((r) => setForm(r.data));
  }, []);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    await updateTournamentSettings({
      name: form.name,
      tournament_date: form.tournament_date || null,
      registration_deadline: form.registration_deadline || null,
      registration_fee: parseInt(form.registration_fee),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (!form) return <p className="text-muted">Loading...</p>;

  return (
    <form className="tournament-tab card" onSubmit={save}>
      <h3>Tournament Settings</h3>
      <div className="form-grid-2">
        <div className="field">
          <label>Tournament Name</label>
          <input value={form.name} onChange={set("name")} required />
        </div>
        <div className="field">
          <label>Registration Fee ($)</label>
          <input
            type="number"
            min="0"
            value={form.registration_fee}
            onChange={set("registration_fee")}
            required
          />
        </div>
        <div className="field">
          <label>Tournament Date</label>
          <input
            type="date"
            value={form.tournament_date || ""}
            onChange={set("tournament_date")}
          />
        </div>
        <div className="field">
          <label>Registration Deadline</label>
          <input
            type="date"
            value={form.registration_deadline || ""}
            onChange={set("registration_deadline")}
          />
        </div>
      </div>
      <div className="tournament-footer">
        <button type="submit" className="btn-primary">
          Save Settings
        </button>
        {saved && <span className="success-msg">Saved</span>}
      </div>
    </form>
  );
}

// ── Main Admin Page ───────────────────────────────────────
export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!localStorage.getItem("admin_token"));
  const [tab, setTab] = useState("players");
  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState({
    status: "pending",
    team_id: "",
    search: "",
  });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cardModal, setCardModal] = useState(null); // { player, mode: "view" | "generate" }

  const openCard = (p) =>
    setCardModal({ player: p, mode: p.card_url ? "view" : "generate" });

  const loadStats = () => fetchStats().then((r) => setStats(r.data));
  const loadTeamFilter = () => fetchAdminTeams().then((r) => setTeams(r.data));

  const loadPlayers = useCallback(() => {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.team_id) params.team_id = filter.team_id;
    if (filter.search) params.search = filter.search;
    fetchPlayers(params).then((r) => setPlayers(r.data));
  }, [filter]);

  useEffect(() => {
    if (!authed) return;
    loadStats();
    loadTeamFilter();
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    loadPlayers();
  }, [authed, loadPlayers]);

  const handleApprove = async (p) => {
    await approvePlayer(p.id);
    loadPlayers();
    loadStats();
  };
  const handleReject = (p) => setRejectTarget(p);
  const confirmReject = async (note) => {
    await rejectPlayer(rejectTarget.id, note);
    setRejectTarget(null);
    loadPlayers();
    loadStats();
  };
  const handleDelete = (p) => setDeleteTarget(p);
  const confirmDelete = async () => {
    await deletePlayer(deleteTarget.id);
    setDeleteTarget(null);
    loadPlayers();
    loadStats();
  };

  const [exportingCards, setExportingCards] = useState(false);
  const handleExportCards = async () => {
    setExportingCards(true);
    try {
      const res = await exportAllCards(
        filter.team_id ? { team_id: filter.team_id } : {},
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "id_cards.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("ID 카드 ZIP 다운로드에 실패했습니다.");
    } finally {
      setExportingCards(false);
    }
  };

  if (!authed) return <LoginForm onLogin={() => setAuthed(true)} />;

  return (
    <div className="admin-page">
      <nav className="admin-nav">
        <div className="admin-nav-inner">
          <div className="admin-brand">
            <img
              src="/images/kasausa_icon.png"
              alt="KFAUS"
              className="nav-logo-img"
            />
            <div className="nav-org">
              재미대한축구협회
              <span>Korean-american soccer association</span>
            </div>
          </div>
          <div className="nav-tabs">
            {["players", "teams", "tournament"].map((t) => (
              <button
                key={t}
                className={`nav-tab ${tab === t ? "active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <button
            className="btn-ghost logout-btn"
            onClick={() => {
              localStorage.removeItem("admin_token");
              setAuthed(false);
              navigate("/");
            }}
          >
            Log out
          </button>
        </div>
      </nav>

      <main className="admin-main">
        {stats && (
          <div className="stats-row">
            {[
              {
                label: "Total",
                value: stats.total_registrations,
              },
              {
                label: "Pending",
                value: stats.pending,
                cls: "yellow",
              },
              {
                label: "Approved",
                value: stats.approved,
                cls: "green",
              },
              {
                label: "Rejected",
                value: stats.rejected,
                cls: "red",
              },
              { label: "Paid", value: stats.paid, cls: "green" },
              { label: "Teams", value: stats.total_teams },
            ].map((s) => (
              <div className={`stat-card card ${s.cls || ""}`} key={s.label}>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "teams" && (
          <TeamsTab
            onSelectTeam={(t) => {
              setFilter((f) => ({
                ...f,
                team_id: String(t.id),
                status: "",
              }));
              setTab("players");
            }}
          />
        )}
        {tab === "tournament" && <TournamentTab />}

        {tab === "players" && (
          <>
            <div className="filters card">
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    status: e.target.value,
                  }))
                }
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select
                value={filter.team_id}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    team_id: e.target.value,
                  }))
                }
              >
                <option value="">All Teams</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Search name..."
                value={filter.search}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    search: e.target.value,
                  }))
                }
              />
              <button
                className="btn-ghost export-btn"
                onClick={() =>
                  exportPlayers({
                    status: filter.status,
                    team_id: filter.team_id,
                  })
                }
              >
                <IconDownload /> Export CSV
              </button>
              <button
                className="btn-ghost export-btn"
                onClick={handleExportCards}
                disabled={exportingCards}
              >
                <IconDownload />{" "}
                {exportingCards ? "준비 중..." : "ID Cards (ZIP)"}
              </button>
              <button
                className="btn-ghost export-btn"
                onClick={() =>
                  window.open(
                    "/pdf/substitution_form.pdf",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <IconDownload /> Substitution Form
              </button>
            </div>

            <div className="table-wrap card">
              <table className="players-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Team</th>
                    <th>DOB</th>
                    <th>Pos / #</th>
                    <th>Nationality</th>
                    <th>Status</th>
                    {/* <th>Payment</th> */}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {players.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-row">
                        No players found
                      </td>
                    </tr>
                  ) : (
                    players.map((p) => (
                      <PlayerRow
                        key={p.id}
                        player={p}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onDelete={handleDelete}
                        onView={setViewTarget}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      {rejectTarget && (
        <RejectModal
          player={rejectTarget}
          onConfirm={confirmReject}
          onCancel={() => setRejectTarget(null)}
        />
      )}
      {viewTarget && (
        <PlayerDetailModal
          player={viewTarget}
          onClose={() => setViewTarget(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onMakeCard={openCard}
        />
      )}
      {cardModal?.mode === "generate" && (
        <IdCardGenerateModal
          player={cardModal.player}
          onClose={() => setCardModal(null)}
          onSaved={() => {
            setCardModal((m) => ({ player: m.player, mode: "view" }));
            loadPlayers();
          }}
        />
      )}
      {cardModal?.mode === "view" && (
        <IdCardViewModal
          player={cardModal.player}
          onClose={() => setCardModal(null)}
          onRegenerate={(p) => setCardModal({ player: p, mode: "generate" })}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          player={deleteTarget}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
